import React, { useEffect, useState } from "react";
import { Plus, Trash2, MapPin } from "lucide-react";
import { Button, Input, Select, Card, CardContent, StatusBadge, LoadingBlock, EmptyState, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { AREA_TYPES } from "./constants";
import { Field } from "../onboarding/_ui";

export function ServiceAreasPanel() {
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null);
  const [type, setType] = useState("postal_code");
  const [value, setValue] = useState("");
  const [radius, setRadius] = useState("");
  const [center, setCenter] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.areaList().then(setRows).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async () => {
    setSaving(true);
    try {
      const body: Record<string, unknown> = { area_type: type };
      if (type === "postal_code") { if (!value.trim()) { toast.error("Enter a postal code."); setSaving(false); return; } body.postal_code = value.trim(); }
      if (type === "city") { if (!value.trim()) { toast.error("Enter a city."); setSaving(false); return; } body.city = value.trim(); }
      if (type === "radius") { if (!radius) { toast.error("Enter a radius."); setSaving(false); return; } body.radius_miles = Number(radius); body.center_label = center.trim() || null; }
      await api.areaCreate(body);
      toast.success("Service area added"); setValue(""); setRadius(""); setCenter(""); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (id: string) => { try { await api.areaDelete(id); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const describe = (a: Record<string, unknown>) => {
    if (a.area_type === "postal_code") return `Postal code ${a.postal_code}`;
    if (a.area_type === "city") return `City: ${a.city}`;
    return `${a.radius_miles} mi radius${a.center_label ? ` of ${a.center_label}` : ""}`;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-dashed border-slate-300 p-4">
        <p className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-700"><Plus className="h-4 w-4" /> Add a service area</p>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Type" testid="field-area-type"><div className="w-44"><Select value={type} onChange={setType} options={AREA_TYPES} testid="area-type" /></div></Field>
          {type !== "radius" ? (
            <Field label={type === "postal_code" ? "Postal code" : "City"} testid="field-area-value">
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={type === "postal_code" ? "60601" : "Chicago"} data-testid="area-value" />
            </Field>
          ) : (
            <>
              <Field label="Radius (miles)" testid="field-area-radius"><Input type="number" value={radius} onChange={(e) => setRadius(e.target.value)} className="w-32" data-testid="area-radius" /></Field>
              <Field label="Center (label)" testid="field-area-center"><Input value={center} onChange={(e) => setCenter(e.target.value)} placeholder="Main office" data-testid="area-center" /></Field>
            </>
          )}
          <Button onClick={add} disabled={saving} data-testid="area-add">{saving ? "Adding…" : "Add"}</Button>
        </div>
        {type === "radius" && <p className="mt-2 text-xs text-amber-600">Radius areas are stored but can't be distance-verified yet (geocoding not enabled).</p>}
      </div>

      {rows === null ? <LoadingBlock rows={2} /> : rows.length === 0 ? (
        <EmptyState icon={MapPin} title="No service areas" description="Add postal codes, cities or a radius to define where you work." />
      ) : (
        <Card><CardContent className="divide-y divide-slate-100 p-0" data-testid="area-list">
          {rows.map((a) => (
            <div key={a.id as string} className="flex items-center justify-between px-5 py-3.5" data-testid={`area-item-${a.id}`}>
              <div className="flex items-center gap-2">
                <StatusBadge tone="slate">{(a.area_type as string).replace("_", " ")}</StatusBadge>
                <span className="text-sm text-slate-700">{describe(a)}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove(a.id as string)} data-testid={`area-delete-${a.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>
            </div>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
